// ABOUTME: Pump settings form — three sections: available pumps table, hose length, and default answers.
// ABOUTME: Uses react-hook-form; sections map to three groups on the backend API.
import {Button, ControlledInput, Icon, TextMedium} from '@hm/fundamental'
import {CountryConfigsTooltip} from './CountryConfigsTooltip'
import {mdiInformationOutline, mdiPlus, mdiTrashCanOutline} from '@mdi/js'
import React, {useEffect, useMemo, useRef} from 'react'
import {useForm, useWatch} from 'react-hook-form'

import {useTranslation} from '../../../../hooks/useTranslation'
import {useRowTouchTracking} from '../utils'
import {
  AvailablePumpFormValues,
  DefaultAnswersFormValues,
  DischargeType,
  PumpSettingsConfig,
  PumpSettingsFormValues,
} from '../types/pumpSettings'
import {FilterDropdown} from './FilterDropdown'
import {NumericalInput} from './NumericalInput'
import {CountryConfigsSwitchControl} from './CountryConfigsSwitchControl'

export interface PumpSettingsFormProps {
  config: PumpSettingsConfig
  isReadOnly: boolean
  onDirtyChange: (dirty: boolean) => void
  onValidChange: (valid: boolean) => void
  onValuesChange?: (values: PumpSettingsFormValues) => void
  leadTimeUomOptions: {value: string; label: string}[]
  sizeUomOptions: {value: string; label: string; symbol?: string}[]
  hoseUomOptions: {value: string; label: string; symbol?: string}[]
  referenceEventOptions: {value: string; label: string}[]
  /** Country-default (root) scope: all sections must be filled (no parent to inherit from). */
  isRoot?: boolean
  'data-cy'?: string
}

const EMPTY_PUMP: AvailablePumpFormValues = {
  code: '',
  size: 0,
  minLeadTime: 0,
  leadTimeUomId: '',
  referenceEvent: '',
}

const NOT_OVERRIDDEN = {isOverridden: false, value: null}

const EMPTY_DEFAULT_ANSWERS: DefaultAnswersFormValues = {
  canLargerPumpFit: NOT_OVERRIDDEN,
  isPossibleToCleanPumpOnSite: NOT_OVERRIDDEN,
  isExtraHoseNeeded: NOT_OVERRIDDEN,
  defaultDischarge: NOT_OVERRIDDEN,
}

const DISCHARGE_TYPES: DischargeType[] = ['Boom', 'Hose']

export const toFormValues = (cfg: PumpSettingsConfig): PumpSettingsFormValues => ({
  availablePumps: cfg.availablePumps,
  hoseLength: cfg.hoseLength,
  defaultAnswers: cfg.defaultAnswers
    ? {
        canLargerPumpFit: {
          isOverridden: cfg.defaultAnswers.canLargerPumpFit.isOverridden,
          value: cfg.defaultAnswers.canLargerPumpFit.value ?? true,
        },
        isPossibleToCleanPumpOnSite: {
          isOverridden: cfg.defaultAnswers.isPossibleToCleanPumpOnSite.isOverridden,
          value: cfg.defaultAnswers.isPossibleToCleanPumpOnSite.value ?? true,
        },
        isExtraHoseNeeded: {
          isOverridden: cfg.defaultAnswers.isExtraHoseNeeded.isOverridden,
          value: cfg.defaultAnswers.isExtraHoseNeeded.value ?? true,
        },
        // Optional: not every country uses it, so no fallback value.
        defaultDischarge: cfg.defaultAnswers.defaultDischarge ?? NOT_OVERRIDDEN,
      }
    : null,
})

const PUMPS_COLUMN_HEADERS: {labelKey: string; tooltipKey?: string; className: string}[] = [
  {labelKey: 'countryConfigurations.pumpSettings.pumpName', tooltipKey: 'countryConfigurations.pumpSettings.pumpNameTooltip', className: 'w-[113px] shrink-0'},
  {labelKey: 'countryConfigurations.pumpSettings.size', className: 'w-[140px] shrink-0'},
  {labelKey: 'countryConfigurations.pumpSettings.minLeadTime', tooltipKey: 'countryConfigurations.pumpSettings.minLeadTimeTooltip', className: 'w-[140px] shrink-0'},
  {labelKey: 'countryConfigurations.pumpSettings.leadTimeUom', className: 'w-[183px] shrink-0'},
  {labelKey: 'countryConfigurations.pumpSettings.referenceEvent', tooltipKey: 'countryConfigurations.pumpSettings.referenceEventTooltip', className: 'flex-1 min-w-0'},
  {labelKey: 'countryConfigurations.pumpSettings.actions', className: 'w-[66px] shrink-0'},
]

interface PumpErrors {
  code?: string
  size?: string
  minLeadTime?: string
  leadTimeUomId?: string
  referenceEvent?: string
}

export const PumpSettingsForm: React.FC<PumpSettingsFormProps> = ({
  config,
  isReadOnly,
  onDirtyChange,
  onValidChange,
  onValuesChange,
  leadTimeUomOptions,
  sizeUomOptions,
  hoseUomOptions,
  referenceEventOptions,
  isRoot = false,
  'data-cy': dataCy,
}) => {
  const {t} = useTranslation()
  const initRowIdCounter = useRef(0)
  const initNextRowId = () => String(initRowIdCounter.current++)
  const formDefaultValues = useMemo(
    () => {
      const base = toFormValues(config)
      const values: PumpSettingsFormValues = {
        ...base,
        availablePumps: config.availablePumps
          ? {
              ...config.availablePumps,
              pumps: config.availablePumps.pumps.map((p) => ({...p, _rowId: initNextRowId()})),
            }
          : null,
      }
      if (!isRoot) return values
      // Root has no parent: everything the form displays must be sent as overridden.
      const answer = (a?: {value: boolean | null} | null) => ({isOverridden: true, value: a?.value ?? true})
      const hoseUom = base.hoseLength?.uomId?.value ?? hoseUomOptions[0]?.value ?? null
      return {
        ...values,
        hoseLength: {
          uomId: {isOverridden: true, value: hoseUom},
          default: base.hoseLength?.default ?? {isOverridden: false, value: null},
          min: base.hoseLength?.min ?? {isOverridden: false, value: null},
          max: base.hoseLength?.max ?? {isOverridden: false, value: null},
          step: base.hoseLength?.step ?? {isOverridden: false, value: null},
        },
        defaultAnswers: {
          canLargerPumpFit: answer(base.defaultAnswers?.canLargerPumpFit),
          isPossibleToCleanPumpOnSite: answer(base.defaultAnswers?.isPossibleToCleanPumpOnSite),
          isExtraHoseNeeded: answer(base.defaultAnswers?.isExtraHoseNeeded),
          defaultDischarge: base.defaultAnswers?.defaultDischarge ?? NOT_OVERRIDDEN,
        },
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config, isRoot],
  )
  const {control, setValue, reset, formState: {isDirty}} = useForm<PumpSettingsFormValues>({
    defaultValues: formDefaultValues,
  })

  useEffect(() => {
    reset(formDefaultValues)
  }, [formDefaultValues, reset])

  const availablePumps = useWatch({control, name: 'availablePumps'})
  const hoseLength = useWatch({control, name: 'hoseLength'})
  const defaultAnswers = useWatch({control, name: 'defaultAnswers'})
  const onValuesChangeRef = useRef(onValuesChange)

  useEffect(() => {
    onValuesChangeRef.current = onValuesChange
  }, [onValuesChange])

  const pumps = availablePumps?.pumps ?? []
  const pumpsLength = pumps.length
  const pumpSizeUomId = availablePumps?.pumpSizeUomId || sizeUomOptions[0]?.value || ''
  // Keep the default pump valid: the dropdown is disabled with a single pump, so pick it automatically.
  const resolveDefaultPump = (list: AvailablePumpFormValues[], current: string) =>
    list.some((p) => p.code && p.code === current) ? current : (list.find((p) => p.code)?.code ?? '')
  const {touchedRows, markTouched, removeTouched, newRowRef, nextRowId, markAdded} = useRowTouchTracking(
    pumpsLength,
    config.availablePumps?.pumps.length ?? 0,
  )

  const pumpErrors = useMemo<PumpErrors[]>(() => {
    return pumps.map((pump) => {
      const errs: PumpErrors = {}
      if (!pump.code.trim()) {
        errs.code = t('countryConfigurations.pumpSettings.validation.required')
      }
      if (pump.size === null || pump.size <= 0) {
        errs.size = t('countryConfigurations.pumpSettings.validation.sizePositive')
      }
      if (pump.minLeadTime !== null && pump.minLeadTime < 0) {
        errs.minLeadTime = t('countryConfigurations.pumpSettings.validation.nonNegative')
      }
      if (!pump.leadTimeUomId) {
        errs.leadTimeUomId = t('countryConfigurations.pumpSettings.validation.required')
      }
      if (!pump.referenceEvent) {
        errs.referenceEvent = t('countryConfigurations.pumpSettings.validation.required')
      }
      return errs
    })
  }, [pumps, t])

  const hoseErrors = useMemo<Partial<Record<'default' | 'min' | 'max' | 'step', string>>>(() => {
    const errs: Partial<Record<'default' | 'min' | 'max' | 'step', string>> = {}

    if (hoseLength === null) {
      return errs
    }

    const defVal = hoseLength?.default?.value ?? 0
    const minVal = hoseLength?.min?.value ?? 0
    const maxVal = hoseLength?.max?.value ?? 0
    const stepVal = hoseLength?.step?.value ?? 0

    if (defVal <= 0) {
      errs.default = t('countryConfigurations.pumpSettings.validation.sizePositive')
    } else if (minVal > 0 && defVal < minVal) {
      errs.default = t('countryConfigurations.pumpSettings.validation.hoseDefaultBelowMin')
    } else if (maxVal > 0 && defVal > maxVal) {
      errs.default = t('countryConfigurations.pumpSettings.validation.hoseDefaultAboveMax')
    }

    if (minVal <= 0) errs.min = t('countryConfigurations.pumpSettings.validation.sizePositive')
    else if (maxVal > 0 && minVal > maxVal) errs.min = t('countryConfigurations.pumpSettings.validation.hoseMinExceedsMax')
    if (maxVal <= 0) errs.max = t('countryConfigurations.pumpSettings.validation.sizePositive')
    if (stepVal <= 0) errs.step = t('countryConfigurations.pumpSettings.validation.sizePositive')

    return errs
  }, [hoseLength, t])

  const defaultPumpCode = availablePumps?.defaultPumpCode ?? ''
  const pumpsValid =
    (pumps.length === 0 && !config._meta.isInherited) ||
    (pumps.length > 0 &&
      pumpErrors.every((e) => Object.keys(e).length === 0) &&
      defaultPumpCode.trim().length > 0 &&
      pumps.some((p) => p.code === defaultPumpCode))
  // At root there is no parent to inherit from: every section must be provided before saving.
  const rootSectionsValid =
    !isRoot ||
    ([availablePumps, hoseLength, defaultAnswers].every((section) => section !== null && section !== undefined) &&
      pumps.length > 0)
  const sizeUomError = pumps.length > 0 && !pumpSizeUomId
    ? t('countryConfigurations.pumpSettings.validation.sizeUomRequired')
    : undefined
  const hoseUomError = hoseLength && !hoseLength.uomId?.value && (isRoot || hoseLength.uomId?.isOverridden)
    ? t('countryConfigurations.pumpSettings.validation.hoseUomRequired')
    : undefined
  const isValid = pumpsValid && Object.values(hoseErrors).every((e) => !e) && rootSectionsValid && !sizeUomError && !hoseUomError

  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  useEffect(() => {
    onValidChange(isValid)
  }, [isValid, onValidChange])

  useEffect(() => {
    onValuesChangeRef.current?.({availablePumps, hoseLength, defaultAnswers})
  }, [availablePumps, hoseLength, defaultAnswers])

  const handleAddPump = () => {
    markAdded()
    setValue('availablePumps', {
      pumpSizeUomId,
      defaultPumpCode: availablePumps?.defaultPumpCode ?? '',
      pumps: [...pumps, {...EMPTY_PUMP, _rowId: nextRowId()}],
    }, {shouldDirty: true})
  }

  const handleRemovePump = (index: number) => {
    const removedId = pumps[index]?._rowId
    const remaining = pumps.filter((_, i) => i !== index)
    setValue('availablePumps', {
      pumpSizeUomId,
      defaultPumpCode: resolveDefaultPump(remaining, availablePumps?.defaultPumpCode ?? ''),
      pumps: remaining,
    }, {shouldDirty: true})
    if (removedId) removeTouched(removedId)
  }

  const hoseSuffix = hoseUomOptions.find(o => o.value === (hoseLength?.uomId?.value ?? ''))?.symbol ?? ''
  const sizeSuffix = sizeUomOptions.find(o => o.value === pumpSizeUomId)?.symbol ?? ''

  const handlePumpChange = <K extends keyof AvailablePumpFormValues>(
    index: number,
    field: K,
    value: AvailablePumpFormValues[K],
  ) => {
    const newPumps = pumps.map((pump, i) =>
      i === index ? {...pump, [field]: value} : pump,
    )
    setValue('availablePumps', {
      pumpSizeUomId,
      defaultPumpCode: resolveDefaultPump(newPumps, availablePumps?.defaultPumpCode ?? ''),
      pumps: newPumps,
    }, {shouldDirty: true})
  }

  return (
    <div className="flex flex-col gap-4 p-6 w-full" data-cy={dataCy ?? 'pump-settings-form'}>
      {/* Section 1: Available pumps */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-1">
          <TextMedium className="font-semibold">
            {t('countryConfigurations.pumpSettings.pumpDefaults')}
          </TextMedium>
          <CountryConfigsTooltip content={t('countryConfigurations.pumpSettings.pumpDefaultsTooltip')} delayMs={300}>
            <span className="text-foreground flex items-center cursor-default">
              <Icon path={mdiInformationOutline} size="regular" />
            </span>
          </CountryConfigsTooltip>
        </div>

        {/* Size UoM + default pump code row */}
        <div className="flex items-center gap-4 h-12">
          <div className="flex items-center gap-1 w-[280px] shrink-0">
            <TextMedium>
              {t('countryConfigurations.pumpSettings.defaultPump')}
            </TextMedium>
            <CountryConfigsTooltip content={t('countryConfigurations.pumpSettings.defaultPumpTooltip')} delayMs={300}>
              <span className="text-foreground flex items-center cursor-default">
                <Icon path={mdiInformationOutline} size="regular" />
              </span>
            </CountryConfigsTooltip>
          </div>
          <div className="w-[200px]">
            <FilterDropdown
              options={pumps.filter(p => p.code).map(p => ({value: p.code, label: p.code}))}
              value={availablePumps?.defaultPumpCode ?? ''}
              onChange={(val) =>
                setValue('availablePumps', {
                  pumpSizeUomId,
                  defaultPumpCode: val ?? '',
                  pumps,
                }, {shouldDirty: true})
              }
              disabled={isReadOnly || pumps.filter(p => p.code).length <= 1}
              data-cy="pump-settings-default-pump"
            />
          </div>
        </div>

        {/* Pumps sub-section title */}
        <div className="flex items-center gap-1 mt-2">
          <TextMedium className="font-semibold">
            {t('countryConfigurations.pumpSettings.pumpsAndLeadTimes')}
          </TextMedium>
          <CountryConfigsTooltip content={t('countryConfigurations.pumpSettings.pumpsAndLeadTimesTooltip')} delayMs={300}>
            <span className="text-foreground flex items-center cursor-default">
              <Icon path={mdiInformationOutline} size="regular" />
            </span>
          </CountryConfigsTooltip>
        </div>

        {/* Add pump button */}
        {!isReadOnly && (
          <Button
            variant="secondary"
            size="small"
            iconPrefix={mdiPlus}
            onClick={handleAddPump}
            data-cy="pump-settings-add-pump"
            className="w-full"
          >
            {t('countryConfigurations.pumpSettings.addPump')}
          </Button>
        )}
        {isRoot && pumps.length === 0 && (
          <p role="alert" className="text-xs leading-4 text-negative" data-cy="pump-settings-root-empty">
            {t('countryConfigurations.pumpSettings.validation.atLeastOnePump')}
          </p>
        )}
        {sizeUomError && <p role="alert" className="text-xs leading-4 text-negative">{sizeUomError}</p>}

        {/* Pumps table */}
        {pumps.length > 0 && (
          <div className="w-full relative z-10">
            {/* Column headers */}
            <div className="flex bg-columnheader_background rounded-t-md">
              {PUMPS_COLUMN_HEADERS.map((col) => (
                <div key={col.labelKey} className={`${col.className} px-2 py-2 flex items-center gap-1`}>
                  <TextMedium className="text-foreground text-sm font-normal whitespace-nowrap">
                    {t(col.labelKey)}
                  </TextMedium>
                  {col.tooltipKey && (
                    <CountryConfigsTooltip content={t(col.tooltipKey)} delayMs={300}>
                      <span className="text-foreground flex items-center cursor-default">
                        <Icon path={mdiInformationOutline} size="regular" />
                      </span>
                    </CountryConfigsTooltip>
                  )}
                </div>
              ))}
            </div>

            {/* Rows */}
            {pumps.map((pump, index) => {
              const rowErrors = pumpErrors[index] ?? {}
              const rowId = pump._rowId ?? String(index)
              return (
                <div
                  key={index}
                  className="flex border-b border-border"
                  data-cy={`pump-settings-pump-${index}`}
                >
                  {/* Code */}
                  <div className="w-[113px] shrink-0 px-2 h-14 flex items-center">
                    <CountryConfigsTooltip content={touchedRows.has(rowId) ? (rowErrors.code ?? '') : ''} delayMs={300}>
                      <div className="w-full" ref={index === pumps.length - 1 ? newRowRef : undefined}>
                        <ControlledInput
                          value={pump.code}
                          onChange={(e) => handlePumpChange(index, 'code', e.target.value)}
                          onBlur={() => markTouched(rowId)}
                          isDisabled={isReadOnly}
                          variant="small"
                          error={touchedRows.has(rowId) ? rowErrors.code : undefined}
                          isTouched={false}
                          data-cy={`pump-settings-pump-${index}-code`}
                          className="w-full"
                        />
                      </div>
                    </CountryConfigsTooltip>
                  </div>

                  {/* Size */}
                  <div className="w-[140px] shrink-0 px-2 h-14 flex items-center">
                    <CountryConfigsTooltip content={touchedRows.has(rowId) ? (rowErrors.size ?? '') : ''} delayMs={300}>
                      <div className="w-full">
                        <NumericalInput
                          value={pump.size ?? 0}
                          onChange={(val) => handlePumpChange(index, 'size', val)}
                          onBlur={() => markTouched(rowId)}
                          min={1}
                          suffix={sizeSuffix}
                          disabled={isReadOnly}
                          hasError={!!(touchedRows.has(rowId) && rowErrors.size)}
                          data-cy={`pump-settings-pump-${index}-size`}
                        />
                      </div>
                    </CountryConfigsTooltip>
                  </div>

                  {/* Min lead time */}
                  <div className="w-[140px] shrink-0 px-2 h-14 flex items-center">
                    <NumericalInput
                      value={pump.minLeadTime ?? 0}
                      onChange={(val) => handlePumpChange(index, 'minLeadTime', val)}
                      disabled={isReadOnly}
                      error={rowErrors.minLeadTime}
                      data-cy={`pump-settings-pump-${index}-minLeadTime`}
                    />
                  </div>

                  {/* Lead time UoM */}
                  <div className="w-[183px] shrink-0 px-2 h-14 flex items-center">
                    <FilterDropdown
                      options={leadTimeUomOptions}
                      value={pump.leadTimeUomId}
                      onChange={(val) => handlePumpChange(index, 'leadTimeUomId', val ?? '')}
                      disabled={isReadOnly}
                      data-cy={`pump-settings-pump-${index}-leadTimeUomId`}
                    />
                  </div>

                  {/* Reference event */}
                  <div className="flex-1 min-w-0 px-2 h-14 flex items-center">
                    <FilterDropdown
                      options={referenceEventOptions}
                      value={pump.referenceEvent !== null && pump.referenceEvent !== undefined ? String(pump.referenceEvent) : ''}
                      onChange={(val) => handlePumpChange(index, 'referenceEvent', val)}
                      size="medium"
                      disabled={isReadOnly}
                      data-cy={`pump-settings-pump-${index}-referenceEvent`}
                    />
                  </div>

                  {/* Actions */}
                  <div className="w-[66px] shrink-0 px-2 h-14 flex items-center justify-center">
                    {!isReadOnly && (
                      <Button
                        variant="secondary"
                        square
                        outline
                        size="regular"
                        onClick={() => handleRemovePump(index)}
                        aria-label={`Delete pump ${index + 1}`}
                        data-cy={`pump-settings-pump-${index}-delete`}
                      >
                        <Icon path={mdiTrashCanOutline} size="regular" />
                      </Button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Section 2: Hose length */}
      <div className="flex flex-col gap-3">
        <TextMedium className="font-semibold">
          {t('countryConfigurations.pumpSettings.hoseLength')}
        </TextMedium>
        {hoseUomError && <p role="alert" className="text-xs leading-4 text-negative">{hoseUomError}</p>}
        <div className="flex flex-col gap-0">
          {(['default', 'min', 'max', 'step'] as const).map((field) => {
            const capitalizedField = field.charAt(0).toUpperCase() + field.slice(1)
            return (
            <div key={field} className="flex items-center gap-4 h-12">
              <div className="flex items-center gap-1 w-[280px] shrink-0">
                <TextMedium className="text-foreground text-sm">
                  {t(`countryConfigurations.pumpSettings.hose${capitalizedField}`)}
                </TextMedium>
                <CountryConfigsTooltip content={t(`countryConfigurations.pumpSettings.hose${capitalizedField}Tooltip`)} delayMs={300}>
                  <span className="text-foreground flex items-center cursor-default">
                    <Icon path={mdiInformationOutline} size="regular" />
                  </span>
                </CountryConfigsTooltip>
              </div>
              <div className="w-[400px]">
                <CountryConfigsTooltip content={hoseErrors[field] ?? ''} delayMs={300}>
                  <div>
                    <NumericalInput
                      value={hoseLength?.[field]?.value ?? 0}
                      onChange={(val) =>
                        setValue('hoseLength', {
                          ...(hoseLength ?? {
                            uomId: {isOverridden: false, value: null},
                            default: {isOverridden: false, value: null},
                            min: {isOverridden: false, value: null},
                            max: {isOverridden: false, value: null},
                            step: {isOverridden: false, value: null},
                          }),
                          ...(hoseLength?.uomId?.value ? {} : {uomId: {isOverridden: true, value: hoseUomOptions[0]?.value ?? null}}),
                          [field]: {isOverridden: true, value: val},
                        }, {shouldDirty: true})
                      }
                      min={0}
                      disabled={isReadOnly}
                      hasError={!!hoseErrors[field]}
                      suffix={hoseSuffix}
                      size="large"
                      data-cy={`pump-settings-hose-${field}`}
                    />
                  </div>
                </CountryConfigsTooltip>
              </div>
            </div>
            )
          })}
        </div>
      </div>

      <hr className="border-border" />

      {/* Section 3: Default answers */}
      <div className="flex flex-col gap-3">
        <TextMedium className="font-semibold">
          {t('countryConfigurations.pumpSettings.defaultAnswers')}
        </TextMedium>
        <div className="flex flex-col gap-0">
          {(['canLargerPumpFit', 'isPossibleToCleanPumpOnSite', 'isExtraHoseNeeded'] as const).map((field) => (
            <div key={field} className="flex items-center gap-4 h-12">
              <div className="flex items-center gap-1 w-[280px] shrink-0">
                <TextMedium className="text-foreground text-sm">
                  {t(`countryConfigurations.pumpSettings.${field}`)}
                </TextMedium>
                <CountryConfigsTooltip content={t(`countryConfigurations.pumpSettings.${field}Tooltip`)} delayMs={300}>
                  <span className="text-foreground flex items-center cursor-default">
                    <Icon path={mdiInformationOutline} size="regular" />
                  </span>
                </CountryConfigsTooltip>
              </div>
              <CountryConfigsSwitchControl
                value={
                  defaultAnswers?.[field]?.value !== null && defaultAnswers?.[field]?.value !== undefined
                    ? String(defaultAnswers[field].value)
                    : 'true'
                }
                options={[
                  {value: 'true', label: t('countryConfigurations.pumpSettings.yes')},
                  {value: 'false', label: t('countryConfigurations.pumpSettings.no')},
                ]}
                onChange={(val) =>
                  setValue('defaultAnswers', {
                    ...(defaultAnswers ?? EMPTY_DEFAULT_ANSWERS),
                    [field]: {isOverridden: true, value: val === 'true'},
                  }, {shouldDirty: true})
                }
                disabled={isReadOnly}
                data-cy={`pump-settings-${field}`}
              />
            </div>
          ))}
          <div className="flex items-center gap-4 h-12">
            <div className="flex items-center gap-1 w-[280px] shrink-0">
              <TextMedium className="text-foreground text-sm">
                {t('countryConfigurations.pumpSettings.defaultDischarge')}
              </TextMedium>
              <CountryConfigsTooltip content={t('countryConfigurations.pumpSettings.defaultDischargeTooltip')} delayMs={300}>
                <span className="text-foreground flex items-center cursor-default">
                  <Icon path={mdiInformationOutline} size="regular" />
                </span>
              </CountryConfigsTooltip>
            </div>
            <div className="w-[200px]">
              <FilterDropdown
                options={DISCHARGE_TYPES.map((type) => ({
                  value: type,
                  label: t(`countryConfigurations.pumpSettings.discharge${type}`),
                }))}
                value={defaultAnswers?.defaultDischarge?.value ?? ''}
                allValue=""
                placeholder="–"
                openUpwards
                onChange={(val) =>
                  setValue('defaultAnswers', {
                    ...(defaultAnswers ?? EMPTY_DEFAULT_ANSWERS),
                    // Clearing sends "not overridden": inherit at child scopes, not set at the country default.
                    defaultDischarge: val ? {isOverridden: true, value: val as DischargeType} : NOT_OVERRIDDEN,
                  }, {shouldDirty: true})
                }
                disabled={isReadOnly}
                aria-label={t('countryConfigurations.pumpSettings.defaultDischarge')}
                data-cy="pump-settings-defaultDischarge"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
